












































































import {inspect} from 'util'
import Vue from 'vue'
import {reactive, computed, defineComponent, onMounted, set, ref, SetupContext} from '@vue/composition-api'
import moment from 'moment'
import {db, storage} from '@/horizon-firebase'
import {Account, AccountLevel, AccountStatus, UserFeedback} from '@horizon/common/models'
import {timestampToMoment} from '@horizon/common/time'
import {userStore} from '@/store'
import buildInfo from '@/assets/build-info.json'
import firebase from 'firebase/compat/app'
import {backendTaskDocPath, BackendTaskGroup} from '@horizon/common/tasks'

const components = {}

type AuthUser = firebase.User
type UserList = {[uid: string]: AuthUser}
type AccountList = {[uid: string]: Account}

function sortTimeFields(a: TimeField, b: TimeField) {
  return (a?.t.valueOf() || 0) - (b?.t.valueOf() || 0)
}

function sortDurationFields(a: DurationField|null, b: DurationField|null) {
  if (b === null && a === null)
    return 0
  if (b === null)
    return 1
  if (a === null)
    return -1
  return (a.t.asMilliseconds() || 0) - (b.t.asMilliseconds() || 0)
}

async function adminGetUserAccount(uids: string|string[]) {
  const adminGetUserAccount = firebase.functions().httpsCallable('adminGetUserAccount')
  try {
    return (await adminGetUserAccount(uids)).data as {[uid: string]: {user: AuthUser; account: Account}}
  } catch (err) {
    console.log(`Error getting user account info for ${uids}: code ${err.code}, "${err.message}", details: ${err.details}`)
    return {}
  }
}

class TimeField {
  t: moment.Moment
  constructor(t: moment.Moment) {
    this.t = t
  }
  toString() {
    // return this.t.fromNow()
    return this.t?.format('MMM D YYYY h:mma') || '???'
  }
}

class DurationField {
  t: moment.Duration
  constructor(t: moment.Duration) {
    this.t = t
  }
  toString() {
    // return this.t.humanize()
    return moment.utc(this.t.asMilliseconds()).format('H[h]mm[m]ss[s]')
  }
}


type TableRow = {
  uid: string;
  name: string;
  email: string;
  login: TimeField;
  credits: number;
  level: AccountLevel;
  status: AccountStatus;
}

const headers = [
  {text: 'UserID', align: 'start', value: 'uid'},
  {text: 'Name', value: 'displayName'},
  {text: 'Email', value: 'email'},
  {text: 'Level', value: 'level'},
  {text: 'Acct Status', value: 'status'},
  {text: 'Credits', value: 'credits'},
  {text: 'Last Login', value: 'login', sort: sortTimeFields}
]

function fillTableRows(users: UserList, accounts: AccountList, accountTableData: TableRow[]) {
  accountTableData.splice(0, accountTableData.length)
  for (const uid of Object.keys(users)) {
    const user = users[uid]
    const account = accounts[uid]
    if (account) {
      accountTableData.push({uid: uid,
                             name: user?.displayName || '<unknown>',
                             email: user?.email || '<none>',
                             login: new TimeField(timestampToMoment({_seconds: (account.lastLogin as any)._seconds,
                                                                     _nanoseconds: (account.lastLogin as any)._nanoseconds})),
                             credits: account.creditsAvailable,
                             level: account.level,
                             status: account.status})
    } else {
      accountTableData.push({uid: uid,
                             name: user?.displayName || '<unknown>',
                             email: user?.email || '<none>',
                             login: new TimeField(moment('2000-01-01')),
                             credits: 0,
                             level: 'anonymous',
                             status: 'active'})
    }
  }
}

type UnPromisify<T> = T extends Promise<infer U> ? U : T;
type UserAccountInfo = UnPromisify<ReturnType<typeof adminGetUserAccount>>

/** get all user and account infos, by uid */
async function getUserAccountInfo() {
  console.time('getting userAccountInfo')
  const query = await db().collection('users')
    .orderBy('updated', 'desc')
    .limit(50)
    .get()
  console.timeEnd('getting userAccountInfo')
  const uids: string[] = []
  query.forEach(async doc => {
    if (!doc.exists)
      return
    const uid = doc.id
    uids.push(uid)
  })
  console.time('getting adminGetUserAccount')
  const accountInfos = await adminGetUserAccount(uids)
  console.timeEnd('getting adminGetUserAccount')
  return accountInfos
}

async function getUsers(accountInfos: UserAccountInfo, users: UserList, accounts: AccountList, accountTableData: TableRow[]) {
  accountTableData.splice(0, accountTableData.length)
  for (const uid of Object.keys(accountInfos)) {
    const accountInfo = accountInfos[uid]
    if (accountInfo.user)
      set(users, uid, accountInfo.user as AuthUser)
    if (accountInfo.account)
      set(accounts, uid, accountInfo.account as Account)
  }
  fillTableRows(users, accounts, accountTableData)
}

type RenderTableRow = {
  uid: string;
  name: string;
  type: string;
  frames: number;
  resolution: string;
  quality: string;
  status: string;
  started: TimeField;
  finished: TimeField | null;
  elapsed: DurationField;
  renderId: string;
  resultUrl: string;
}

const renderHeaders = [
  {text: 'UserID', align: 'start', value: 'uid'},
  {text: 'Name', value: 'name'},
  {text: 'Type', value: 'type'},
  {text: 'Frames', value: 'frames'},
  {text: 'Resolution', value: 'resolution'},
  {text: 'Quality', value: 'quality'},
  {text: 'Status', value: 'status'},
  {text: 'Started', value: 'started', sort: sortTimeFields},
  {text: 'Finished', value: 'finished', sort: sortTimeFields},
  {text: 'Elapsed', value: 'elapsed', sort: sortDurationFields},
  {text: 'Render', value: 'renderId'},
  {text: 'Result', value: 'resultUrl'}
]

async function getRenders(accountInfos: UserAccountInfo, tableData: RenderTableRow[]) {
  tableData.splice(0, tableData.length)
  try {
    const query = await db().collectionGroup('jobs')
      .get()
    query.forEach(async doc => {
      if (!doc.exists)
        return
      const data = doc.data()
      const startTime = new TimeField(timestampToMoment(data.started))
      const finishTime = data.finished ? new TimeField(timestampToMoment(data.finished)) : null
      const elapsedTime = (startTime && finishTime) ?
        new DurationField(moment.duration(finishTime.t.diff(startTime.t))) :
        new DurationField(moment.duration(0))

      tableData.push({uid: data.userId,
                      name: accountInfos[data.userId]?.user.email || '(none)',
                      type: data.jobType,
                      frames: data.frames,
                      resolution: `${data.resolution.x}x${data.resolution.y}`,
                      quality: data.quality,
                      status: data.status,
                      started: startTime,
                      finished: finishTime,
                      elapsed: elapsedTime,
                      renderId: data.id,
                      resultUrl: ''
      })
      const tableEntry = tableData[tableData.length-1]
      const taskGroupDoc = db().doc(backendTaskDocPath(data.userId, data.id))
      // fill in url in background
      taskGroupDoc.get().then(async doc => {
        const data = doc.data() as BackendTaskGroup
        if (data) {
          try {
            const storageRef = storage().refFromURL(data.resultUrl)
            const videoUrl = await storageRef.getDownloadURL()
            tableEntry.resultUrl = videoUrl
          } catch (err) {
            console.error(`Error getting video URL: ${err}`)
          }
        }
      })
    })
  } catch (err) {
    console.error(err)
  }
}

type FeedbackTableRow = {
  uid: string;
  projectId: string;
  email: string;
  authEmail: string;
  rating: number;
  text: string;
  created: TimeField;
}

const feedbackHeaders = [
  {text: 'UserID', align: 'start', value: 'uid'},
  {text: 'Email', value: 'email'},
  {text: 'Auth Email', value: 'authEmail'},
  {text: 'Rating', value: 'rating'},
  {text: 'Text', value: 'text', width: "30%"},
  {text: 'ProjectID', value: 'projectId'},
  {text: 'Created', value: 'created', sort: sortTimeFields, width: "15em"},
]

async function getFeedback(tableData: FeedbackTableRow[]) {
  tableData.splice(0, tableData.length)
  try {
    const query = await db().collection('userFeedback').get()
    query.forEach(async doc => {
      if (!doc.exists)
        return
      const feedback = UserFeedback.createFromData(doc.data())
      const created = feedback.created && ('seconds' in feedback.created) ?
        new TimeField(timestampToMoment(feedback.created)) :
        new TimeField(moment('2000-01-01'))

      tableData.push({uid: feedback.userId || '(none)',
                      projectId: feedback.projectId || '(none)',
                      email: feedback.email || '(none)',
                      authEmail: feedback.authEmail || '(none)',
                      rating: feedback.rating || 0,
                      text: feedback.text,
                      created})
    })
  } catch (err) {
    console.error(err)
  }
}

export default defineComponent({
  name: 'Admin',
  components,
  setup(props, context: SetupContext) {
    const appName: string = Vue.$appName
    const appVersionInfo = `v${Vue.$appVersion}, build ${buildInfo.buildId} of ${buildInfo.buildDate}`
    const users: UserList = reactive({})
    const accounts: AccountList = reactive({})
    const accountTableData: TableRow[] = reactive([])
    const renderTableData: RenderTableRow[] = reactive([])
    const feedbackTableData: FeedbackTableRow[] = reactive([])
    const loadingData = ref(false)
    const vbp = computed(() => context.root.$vuetify.breakpoint)
    const dataTableContainer: any = ref(null)
    let userAccountInfo: UserAccountInfo = {}
    async function getData() {
      loadingData.value = true
      userAccountInfo = await getUserAccountInfo()
      const promises: Promise<any>[] = []
      promises.push(getUsers(userAccountInfo, users, accounts, accountTableData))
      promises.push(getRenders(userAccountInfo, renderTableData))
      promises.push(getFeedback(feedbackTableData))
      await Promise.all(promises)
      loadingData.value = false
      // console.log(inspect(users))
    }
    onMounted(async () => {
      await getData()
    })

    return {
      appName, appVersionInfo,
      users, accounts,
      headers, accountTableData,
      loadingData, vbp, dataTableContainer,
      renderHeaders, renderTableData,
      feedbackHeaders, feedbackTableData,
      getData
    }
  }
})
